import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Copy, UserPlus } from "lucide-react";

export const RegistrationForm = () => {
  const [districts, setDistricts] = useState([]);
  const [munis, setMunis] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    district: "",
    muniId: "",
  });
  const [muniType, setMuniType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/districts").then((r) => setDistricts(r.data));
  }, []);

  const onDistrict = (val) => {
    setForm((f) => ({ ...f, district: val, muniId: "" }));
    setMuniType("");
    api
      .get(`/districts/${encodeURIComponent(val)}/municipalities`)
      .then((r) => setMunis(r.data));
  };

  const onMuni = (val) => {
    setForm((f) => ({ ...f, muniId: val }));
    const m = munis.find((x) => String(x.id) === val);
    setMuniType(m?.type || "");
  };

  const onPhone = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, phone: v }));
  };

  const phoneInvalid = form.phone.length > 0 && form.phone[0] === "0";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.district || !form.muniId) {
      toast.error("Please fill all required fields");
      return;
    }
    if (phoneInvalid) {
      toast.error("Phone number cannot start with 0");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("ulb_id", form.muniId);
      const { data } = await api.post("/registrations", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("Registration successful!");
      setForm({ name: "", phone: "", district: "", muniId: "" });
      setMuniType("");
      setMunis([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const copyId = () => {
    navigator.clipboard.writeText(result.registration_id);
    toast.success("Registration ID copied");
  };

  return (
    <>
      <Card className="p-5 sm:p-6 border-slate-200">
        <form onSubmit={submit} className="space-y-5">
          {/* Name */}
          <div>
            <Label htmlFor="name">Name / नाम *</Label>
            <Input
              id="name"
              data-testid="registration-name-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Full name"
              className="mt-1.5"
            />
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">Phone No / फ़ोन नंबर *</Label>
            <Input
              id="phone"
              data-testid="registration-phone-input"
              value={form.phone}
              onChange={onPhone}
              placeholder="10-digit mobile"
              inputMode="numeric"
              aria-invalid={phoneInvalid}
              className={`mt-1.5 ${phoneInvalid ? "border-rose-500 focus-visible:ring-rose-500" : ""}`}
            />
            {phoneInvalid && (
              <p className="text-xs text-rose-600 mt-1" data-testid="registration-phone-error">
                Invalid — phone number cannot start with 0
              </p>
            )}
          </div>

          {/* District / Municipality */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>District / ज़िला *</Label>
              <Select value={form.district} onValueChange={onDistrict}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="registration-district-select">
                  <SelectValue placeholder="Select district" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {districts.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Municipality / नगर पालिका *</Label>
              <Select value={form.muniId} onValueChange={onMuni} disabled={!form.district}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="registration-municipality-select">
                  <SelectValue placeholder="Select municipality" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {munis.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Municipality type (auto, read-only) */}
          <div>
            <Label>Municipality Type / नगर पालिका प्रकार</Label>
            <div
              className="mt-1.5 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 flex items-center"
              data-testid="registration-muni-type-display"
            >
              {muniType ? (
                <Badge className="bg-blue-900 hover:bg-blue-900">{muniType}</Badge>
              ) : (
                <span className="text-sm text-muted-foreground">
                  Auto-filled after selecting a municipality
                </span>
              )}
            </div>
          </div>

          <Button
            type="submit"
            data-testid="registration-submit-button"
            className="w-full bg-blue-900 hover:bg-blue-800"
            disabled={loading || phoneInvalid}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" /> Submit Registration
              </>
            )}
          </Button>
        </form>
      </Card>

      {/* Success dialog */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="bg-white" data-testid="success-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-5 w-5" /> Successfully Registered
            </DialogTitle>
            <DialogDescription>Your registration has been recorded. Save your Registration ID.</DialogDescription>
          </DialogHeader>
          {result && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Registration ID
                </p>
                <p
                  className="font-mono font-bold text-lg text-blue-900 mt-1 break-all"
                  data-testid="success-registration-id-display"
                >
                  {result.registration_id}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={copyId}
                  data-testid="copy-registration-id-btn"
                >
                  <Copy className="h-3.5 w-3.5 mr-2" /> Copy ID
                </Button>
              </div>
              <div className="text-sm text-slate-700 space-y-1">
                <p><span className="text-muted-foreground">Name:</span> {result.name}</p>
                <p><span className="text-muted-foreground">District:</span> {result.district}</p>
                <p><span className="text-muted-foreground">Municipality:</span> {result.municipality} ({result.municipality_type})</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              className="w-full bg-blue-900 hover:bg-blue-800"
              onClick={() => setResult(null)}
              data-testid="success-close-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RegistrationForm;
